import * as React from 'react'
import { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography
} from '@mui/material'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import SaveRoundedIcon from '@mui/icons-material/SaveRounded'
import ContentCutRoundedIcon from '@mui/icons-material/ContentCutRounded'
import HighlightForm from './HighlightForm'
import { overlappingHighlights } from '../video/highlightIntervals'
import { useAuth } from '../../auth/AuthContext'
import { listHighlightTags, listStatSuggestions, playerRequest } from '../../service'

HighlightDialog.propTypes = {
  title: PropTypes.string,
  isOpen: PropTypes.bool,
  value: PropTypes.object,
  onSave: PropTypes.func,
  onCancel: PropTypes.func,
  allowedGroups: PropTypes.array,
  existingHighlights: PropTypes.array
}

export default function HighlightDialog ({
  title = 'New Highlight',
  isOpen,
  value = {},
  onSave,
  onCancel,
  allowedGroups = [],
  existingHighlights = []
}) {
  const { user, groups } = useAuth()
  const selectableGroups = groups.filter(group => allowedGroups.includes(group))
  const [open, setOpen] = useState(isOpen)
  const [highlight, setHighlight] = useState({
    ...value,
    groups: value.groups || []
  })
  const [tags, setTags] = useState(value.tags ? value.tags.items.map(item => item.name) : [])
  const [stats, setStats] = useState(value.stats ? value.stats.items.map(item => item.name) : [])
  const [tagOptions, setTagOptions] = useState([])
  const [statOptions, setStatOptions] = useState([])
  const [players, setPlayers] = useState([])

  useEffect(() => {
    let cancelled = false

    const loadSuggestions = async () => {
      const [tagsResult, statsResult, playersResult] = await Promise.allSettled([
        listHighlightTags(100),
        listStatSuggestions(100),
        playerRequest('/players')
      ])

      if (cancelled) return

      if (tagsResult.status === 'fulfilled') {
        setTagOptions((tagsResult.value || []).map(item => item.name))
      }

      if (statsResult.status === 'fulfilled') {
        setStatOptions(statsResult.value || [])
      }
      if (playersResult.status === 'fulfilled') setPlayers(playersResult.value || [])
    }

    loadSuggestions()
    return () => {
      cancelled = true
    }
  }, [])

  const isNew = !value.id
  const nameError = !String(highlight.name || '').trim()
  const parseSeconds = raw => (
    raw === '' || raw === undefined || raw === null ? Number.NaN : Number(raw)
  )
  const start = parseSeconds(highlight.start)
  const end = parseSeconds(highlight.end)
  const rangeError = !Number.isFinite(start) || !Number.isFinite(end) || end <= start
  const canSave = !nameError && !rangeError
  const overlaps = overlappingHighlights(existingHighlights.filter(item => item.id !== value.id), start, end)

  const duration = useMemo(() => (
    !rangeError ? (end - start).toFixed(1) : null
  ), [end, rangeError, start])

  const setField = (key, rawValue) => {
    setHighlight(current => ({ ...current, [key]: rawValue }))
  }

  const handleSave = ev => {
    if (!canSave) return

    const newTags = tags
      .map(tag => String(tag).trim())
      .filter(Boolean)
      .map(tag => {
        const existing = value.tags ? value.tags.items.find(item => item.name === tag) : null
        return existing || { name: tag }
      })

    const newStats = stats
      .map(stat => String(stat).trim())
      .filter(Boolean)
      .map(stat => {
        const existing = value.stats ? value.stats.items.find(item => item.name === stat) : null
        return existing || { name: stat }
      })

    const updated = {
      ...highlight,
      name: String(highlight.name).trim(),
      start,
      end,
      tags: { items: newTags },
      stats: { items: newStats }
    }

    if (onSave) onSave(ev, updated)
    setOpen(false)
  }

  const handleDialogKeyDown = ev => {
    if ((ev.metaKey || ev.ctrlKey) && ev.key === 'Enter') {
      ev.preventDefault()
      handleSave(ev)
    }
  }

  const handleCancel = (ev, reason) => {
    if (reason === 'backdropClick') return
    if (onCancel) onCancel(ev, highlight)
    setOpen(false)
  }

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          backgroundImage: 'none'
        }
      }}
    >
      <DialogTitle sx={{ px: { xs: 2, sm: 2.5 }, py: 1.75 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: 1.5,
              display: 'grid',
              placeItems: 'center',
              bgcolor: 'rgba(85,184,216,.12)',
              color: 'primary.light',
              flexShrink: 0
            }}
          >
            <ContentCutRoundedIcon />
          </Box>

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isNew
                ? value.aiDraft
                  ? 'AI moment (beta). Editing the name, description or range makes it your own highlight, which later analysis runs never change.'
                  : 'Name the clip, confirm the range, then add tags or stats.'
                : 'Update this clip without leaving the review workspace.'}
            </Typography>
          </Box>

          {!rangeError
            ? <Box sx={{ textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
                <Typography variant="caption" color="text.secondary">
                  DURATION
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 750, fontVariantNumeric: 'tabular-nums' }}
                >
                  {duration}s
                </Typography>
              </Box>
            : null}

          <IconButton aria-label="Close highlight dialog" onClick={handleCancel}>
            <CloseRoundedIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent
        dividers
        onKeyDown={handleDialogKeyDown}
        sx={{
          px: { xs: 2, sm: 2.5 },
          py: 2.25,
          maxHeight: 'min(72vh, 720px)'
        }}
      >
        {overlaps.length > 0
          ? <Alert severity="warning" sx={{ mb: 2 }}>
              This range overlaps {overlaps.length} saved highlight{overlaps.length === 1 ? '' : 's'}: {overlaps.map(item => `${item.name} (${item.start}s to ${item.end}s)`).join('; ')}. {value.aiDraft ? 'If one of them was saved from an analysis of this same moment, saving updates it instead of adding a copy; highlights you made or edited are never changed.' : 'You can still save an intentional overlap.'}
            </Alert>
          : null}
        <HighlightForm
          username={user ? user.username : ''}
          groups={selectableGroups}
          players={players}
          value={highlight}
          tags={tags}
          stats={stats}
          tagOptions={tagOptions}
          statOptions={statOptions}
          nameError={nameError}
          rangeError={rangeError}
          onNameChange={ev => setField('name', ev.target.value)}
          onDescriptionChange={ev => setField('description', ev.target.value)}
          onStartChange={ev => setField('start', ev.target.value)}
          onEndChange={ev => setField('end', ev.target.value)}
          onGroupChange={values => setField('groups', values)}
          onPlayerChange={event => setField('playerId', event.target.value)}
          onTagsChange={(_ev, newValues) => setTags(newValues)}
          onStatsChange={(_ev, newValues) => setStats(newValues)}
        />
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2, sm: 2.5 },
          py: 1.5,
          justifyContent: 'space-between',
          bgcolor: 'rgba(255,255,255,.012)'
        }}
      >
        <Stack spacing={0.15} sx={{ display: { xs: 'none', sm: 'block' } }}>
          <Typography variant="caption" color="text.secondary">
            {rangeError
              ? 'Set a valid clip range before saving.'
              : highlight.groups && highlight.groups.length
                ? `Shared with ${highlight.groups.length} group${highlight.groups.length === 1 ? '' : 's'}`
                : 'Private clip'}
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
            ⌘/Ctrl + Enter to save
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1} sx={{ ml: 'auto' }}>
          <Button color="inherit" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!canSave}
            startIcon={<SaveRoundedIcon />}
          >
            {isNew ? 'Create highlight' : 'Save changes'}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  )
}
